/**
 * Reports presentation helpers — PURE, unit-testable functions for money
 * display, period-over-period deltas, and the stock-signal pill mapping. The
 * server owns every number; this only drives how they render. All money math is
 * done in integer CENTS parsed from decimal strings (reusing the review-logic
 * cents utilities) so period comparisons never suffer float drift.
 */
import { toCents, centsToString } from './review-logic'
import type { StockSignal } from './schemas/reports'

/** Format a decimal money string ("1234.56") as "$1,234.56" with grouping. */
export function formatMoney(value: string): string {
  const negative = value.trim().startsWith('-')
  const s = centsToString(toCents(value)) // normalises to 2dp
  const [whole, frac] = s.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${negative ? '-' : ''}$${grouped}.${frac}`
}

/** Percentage string ("30.0") -> "30.0%"; null -> em-dash. */
export function formatPercent(value: string | null): string {
  return value == null ? '—' : `${value}%`
}

export type DeltaDirection = 'up' | 'down' | 'flat'

export interface Delta {
  direction: DeltaDirection
  /** Absolute difference as a 2dp decimal string (current − previous). */
  amount: string
}

/**
 * Period-over-period delta between two decimal strings, computed in cents so it
 * is safe for money and percentages alike. `amount` is the absolute magnitude;
 * `direction` carries the sign.
 */
export function computeDelta(current: string, previous: string): Delta {
  const diff = toCents(current) - toCents(previous)
  const direction: DeltaDirection = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat'
  return { direction, amount: centsToString(Math.abs(diff)) }
}

/**
 * Tailwind text colour for a delta. By default a rise is good (green). Pass
 * `lowerIsBetter` for metrics like fees where a drop is the win, flipping it.
 */
export function deltaClass(direction: DeltaDirection, lowerIsBetter = false): string {
  if (direction === 'flat') return 'text-gray-400'
  const good = lowerIsBetter ? direction === 'down' : direction === 'up'
  return good ? 'text-green-700' : 'text-red-700'
}

/** Arrow glyph for a delta direction. */
export function deltaArrow(direction: DeltaDirection): string {
  return direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→'
}

export interface SignalPill {
  label: string
  /** Tailwind border/bg/text classes for the pill chip. */
  className: string
}

const SIGNAL_PILLS: Record<StockSignal, SignalPill> = {
  RED: {
    label: 'Consider stocking',
    className: 'border-red-300 bg-red-50 text-red-800',
  },
  YELLOW: {
    label: 'Watch closely',
    className: 'border-amber-300 bg-amber-50 text-amber-800',
  },
  GREEN: {
    label: 'Monitor',
    className: 'border-green-300 bg-green-50 text-green-800',
  },
}

/** Map a stock signal to its pill label + colour classes. */
export function signalPill(signal: StockSignal): SignalPill {
  return SIGNAL_PILLS[signal]
}
